import { UnsavedChangesModalProps } from './UnsavedChangesModal.types';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import { Buttons, Message } from '../TwoButtonModal/TwoButtonModal.styles';
import { useNavigate } from 'react-router-dom';

/**
 * Displays a modal to prompt for user confirmation before routing away from forms with unsaved changes.
 * @param unsavedChangesExist boolean; The component rendering the UnsavedChangesModal should track whether unsaved changes exist internally - see EditDsc.tsx
 * @returns FC - Renders a modal component that controls its' own open/closed state
 */
const UnsavedChangesModal: FC<UnsavedChangesModalProps> = ({ 
  unsavedChangesExist, 
  _testingProps 
}) => {
  // Use testing props if available for controlled testing environment
  const [modalOpen, setModalOpen] = useState(_testingProps?.forceOpen || false);
  const [pendingPath, setPendingPath] = useState<string | null>(_testingProps?.pendingPath || null);
  const navigate = useNavigate();
  
  // Setup beforeunload event listener for browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChangesExist) {
        e.preventDefault();
        e.returnValue = ''; // This is required for Chrome
        return ''; // This is required for other browsers
      }
    };

    if (unsavedChangesExist) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedChangesExist]);
  
  // Handle click events for navigation
  useEffect(() => {
    if (!unsavedChangesExist) return;
    
    // For test environments and real usage, we need to intercept link clicks
    const handleClick = (e: MouseEvent) => {
      // Check if the click is on an anchor tag
      const target = e.target as HTMLElement;
      const linkElement = target.closest('a');
      
      if (linkElement && linkElement.getAttribute('href')) {
        // Prevent the default navigation
        e.preventDefault();
        
        // Get the path from the href attribute
        const path = linkElement.getAttribute('href');
        if (path) {
          setPendingPath(path);
          setModalOpen(true);
        }
      }
    };
    
    // Add the click listener
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [unsavedChangesExist]);

  // For testing purposes - update state when testing props change
  useEffect(() => {
    if (_testingProps?.forceOpen !== undefined) {
      setModalOpen(_testingProps.forceOpen);
    }
    if (_testingProps?.pendingPath !== undefined) {
      setPendingPath(_testingProps.pendingPath);
    }
  }, [_testingProps?.forceOpen, _testingProps?.pendingPath]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    // Clear the pending path when closing to help with tests
    setPendingPath(null);
  }, []);

  const handleDiscardClick = useCallback(() => {
    closeModal();
    if (pendingPath) {
      navigate(pendingPath);
    }
  }, [pendingPath, navigate, closeModal]);

  // Render nothing if the modal is not open
  if (!modalOpen) {
    return null;
  }

  return (
    <Modal id='unsavedChangesModal' open={modalOpen} title='There are unsaved changes' onClose={closeModal}>
      <div>
        <Message>Are you sure you want to leave this page?</Message>
        <Buttons>
          <Button variant={Variant.Secondary} label='Keep Editing' onClick={closeModal} data-testid="keep-editing-button" />
          <Button variant={Variant.Primary} label='Discard Changes' onClick={handleDiscardClick} data-testid="discard-changes-button" />
        </Buttons>
      </div>
    </Modal>
  );
};

export default UnsavedChangesModal;
